import { useEffect, useState } from "react";
import { Landmark, CreditCard, ShieldCheck } from "lucide-react";
import StaffLayout from "@/components/staff/StaffLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PAYOUT_FIELDS = [
  ["payout_account_holder", "Account holder", false],
  ["payout_bank_name", "Bank name", false],
  ["payout_iban", "IBAN", false],
  ["payout_swift", "SWIFT / BIC", false],
  ["payout_bank_address", "Bank address", true],
  ["payout_notes", "Internal notes", true],
];

export default function StaffPaymentsPage() {
  const [vals, setVals] = useState({});
  const [recent, setRecent] = useState([]);

  function load() {
    api.get("/settings").then((r) => setVals(r.data));
    api.get("/admin/bookings").then((r) => setRecent((r.data || []).filter((b) => b.payment_status === "paid").slice(0, 20)));
  }
  useEffect(load, []);

  function get(k) { return vals[k]?.value || ""; }
  function set(k, v) { setVals({ ...vals, [k]: { ...(vals[k] || {}), value: v } }); }

  async function save(k) {
    await api.put(`/settings/${k}`, { key: k, value: vals[k]?.value || "" });
    toast.success("Saved");
  }

  async function saveAll() {
    await Promise.all(PAYOUT_FIELDS.map(([k]) => api.put(`/settings/${k}`, { key: k, value: vals[k]?.value || "" })));
    toast.success("All payout details saved");
  }

  return (
    <StaffLayout>
      <h1 className="font-display text-4xl">Payments</h1>
      <p className="text-sm text-[#71717A] mt-1 max-w-2xl">
        Where the money goes. Stripe processes guest payments and pays out to your bank account on its own schedule —
        these details are for your internal records and appear on guest receipts/invoices when needed.
      </p>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <section className="bg-white border border-[#E4E4E7] rounded-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <Landmark size={18} className="text-brand-primary" />
            <h2 className="font-display text-2xl">Payout / bank details</h2>
          </div>
          <div className="space-y-4">
            {PAYOUT_FIELDS.map(([k, label, big]) => (
              <div key={k}>
                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-[#71717A]">{label}</span>
                  {big ? (
                    <textarea data-testid={`payout-${k}`} rows={3} value={get(k)} onChange={(e) => set(k, e.target.value)} className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
                  ) : (
                    <input data-testid={`payout-${k}`} value={get(k)} onChange={(e) => set(k, e.target.value)} className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
                  )}
                </label>
              </div>
            ))}
            <button data-testid="payout-save-all" onClick={saveAll} className="bg-brand-primary text-white px-6 py-2.5 rounded-sm text-sm">Save all</button>
          </div>
        </section>

        <section className="bg-white border border-[#E4E4E7] rounded-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={18} className="text-emerald-700" />
            <h2 className="font-display text-2xl">SumUp processor</h2>
          </div>
          <ul className="text-sm space-y-3 text-[#52525B]">
            <li>· Connected via your SumUp secret API key (set in backend <code className="bg-[#FAFAFA] px-1.5 py-0.5 rounded text-xs">.env</code>)</li>
            <li>· Accepts Visa, Mastercard, Amex, Apple Pay, Google Pay, Klarna (depending on your SumUp plan)</li>
            <li>· Payouts and transaction list available in your <a href="https://me.sumup.com/" target="_blank" rel="noreferrer" className="text-brand-primary hover:underline">SumUp Dashboard</a></li>
            <li>· PCI-DSS compliant — card data never touches our servers</li>
            <li>· Upload your logo at SumUp Dashboard → Account → Business profile so it appears on the checkout page</li>
          </ul>
        </section>
      </div>

      <section className="mt-8 bg-white border border-[#E4E4E7] rounded-md">
        <div className="p-6 border-b flex items-center gap-2">
          <CreditCard size={18} className="text-brand-primary" />
          <h2 className="font-display text-2xl">Recent paid bookings</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-[#FAFAFA] text-xs uppercase tracking-wider text-[#71717A]">
              <tr>
                <th className="text-left p-4">Guest</th>
                <th className="text-left p-4">Room</th>
                <th className="text-left p-4">Stay</th>
                <th className="text-right p-4">Total</th>
                <th className="text-left p-4">Card</th>
                <th className="text-left p-4">Paid on</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {recent.map((b) => (
                <tr key={b.id} data-testid={`payment-row-${b.id}`}>
                  <td className="p-4">
                    <div className="font-medium">{b.guest_name}</div>
                    <div className="text-xs text-[#71717A]">{b.guest_email}</div>
                  </td>
                  <td className="p-4">{b.room_name}</td>
                  <td className="p-4 text-xs">{b.check_in} → {b.check_out}<br />{b.nights} nights</td>
                  <td className="p-4 text-right font-medium">€{b.total.toFixed(2)}</td>
                  <td className="p-4">
                    {b.card_last4 ? (
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <span className="uppercase font-medium tracking-wider">{b.card_brand || "card"}</span>
                        <span className="font-mono">····{b.card_last4}</span>
                      </span>
                    ) : <span className="text-xs text-[#A1A1AA]">—</span>}
                  </td>
                  <td className="p-4 text-xs text-[#71717A]">{(b.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
              {recent.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-sm text-[#71717A]">No paid bookings yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </StaffLayout>
  );
}
